/* ---------------------------------------------------------------------------
   UF PDP — behaviour

   Phase 3  flavour selection -> variant, price, media, stock
   Phase 4  one-time vs subscribe, driven by real Seal selling plan data
   Phase 5  quantity stepper, Add to Cart, stock urgency
   Phase 6  feature pill info popovers
   Phase 7  accordion height animation
   Phase 8  cross-sell tabs and add-to-cart

   ALL SERVER DATA COMES FROM THE JSON ISLAND (snippets/uf-pdp-data.liquid).
   Nothing here re-derives prices or inventory, and nothing hits the network to
   find them. This matters for stock in particular: Shopify strips
   inventory_quantity and inventory_policy from the public variant JSON, so
   they cannot be read from /products/x.js or from a variant:change event —
   only from the island, which is built server-side.

   ADD TO CART IS NOT IMPLEMENTED HERE. The markup uses the theme's own
   <form is="product-form">, so submitting runs the theme's cart logic: cart
   drawer, section bundling, error handling. This file only keeps the form's
   hidden id / selling_plan inputs in sync.
--------------------------------------------------------------------------- */

(function () {
  'use strict';

  /* ------------------------------------------------------------- utilities */

  /** Shopify money format: cents + a format string like "${{amount}}". */
  function formatMoney(cents, format) {
    if (cents == null || isNaN(cents)) return '';

    var value = Number(cents);
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format && format.match(placeholder);
    var name = match ? match[1] : 'amount';

    function withDelimiters(num, precision, thousands, decimal) {
      var fixed = (num / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    var formatted;
    switch (name) {
      case 'amount_no_decimals':
        formatted = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        formatted = withDelimiters(value, 2, ' ', ',');
        break;
      default:
        formatted = withDelimiters(value, 2, ',', '.');
    }

    return format ? format.replace(placeholder, formatted) : formatted;
  }

  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  /* --------------------------------------------------------------- section */

  function initSection(root) {
    var dataEl = $(root, '[data-uf-pdp-data]');
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (err) {
      /* A malformed island would silently break every control below, so fail
         loudly in the console rather than leaving a half-dead buy box. */
      console.error('[uf-pdp] could not parse data island', err);
      return;
    }

    var currentVariantId = String(data.currentVariantId);

    /* CURRENCY-LAYER CALIBRATION.
       Confirmed live on this store: something in the storefront's currency
       stack rewrites the SERVER-rendered price from "$54.95" to "NZ$54.95",
       but leaves anything we write afterwards alone. So the button would flip
       format the instant a customer changed quantity or plan.

       Rather than hardcode "NZ$" or try to identify which app is responsible,
       read back whatever prefix/suffix the page settled on and reuse it. If
       nothing rewrites the price, prefix and suffix stay empty and this is a
       no-op — so it can't break a store that doesn't do this. */
    var moneyPrefix = '';
    var moneySuffix = '';
    var NUMBER_IN_MONEY = /[\d][\d.,  ]*/;

    function money(cents) {
      var base = formatMoney(cents, data.moneyFormat);
      if (!moneyPrefix && !moneySuffix) return base;
      var match = base.match(NUMBER_IN_MONEY);
      return match ? moneyPrefix + match[0] + moneySuffix : base;
    }

    /* BADGE MONEY. "SAVE NZ$10.00" is four characters of currency code and
       three of zeroes that nobody reads, on the one element whose whole job
       is to be glanced at. This drops both and keeps whatever symbol the
       storefront actually uses, rather than hardcoding a dollar sign.

       Cents are dropped, not rounded, so the badge can never claim a saving
       larger than the one being given. */
    function compactMoney(cents) {
      return money(cents)
        .replace(/[A-Za-z]/g, '')
        .replace(/([.,])\d{2}(?!\d)/, '')
        .trim();
    }

    function calibrateMoney() {
      if (!atcPrice) return;

      var variant = data.variants[currentVariantId];
      if (!variant) return;

      var rendered = atcPrice.textContent.trim();
      var ours = formatMoney(activePrice(variant) * currentQuantity(), data.moneyFormat);

      var renderedNumber = rendered.match(NUMBER_IN_MONEY);
      var ourNumber = ours.match(NUMBER_IN_MONEY);

      /* Only trust the sample when both describe the same amount — otherwise
         the page has already moved on and we'd learn the wrong affixes. */
      if (!renderedNumber || !ourNumber) return;
      if (renderedNumber[0].trim() !== ourNumber[0].trim()) return;

      moneyPrefix = rendered.slice(0, renderedNumber.index);
      moneySuffix = rendered.slice(renderedNumber.index + renderedNumber[0].length);
    }

    var form = $(root, 'form.uf-pdp__actions');
    var variantInput = $(root, '[data-uf-variant-input]');

    /* SEAL OWNS THE selling_plan FIELD.
       Confirmed live, not assumed: Seal's app embed targets
       `form[action*=/cart/add]` and, on load, REPLACES our own hidden
       selling_plan input with its own — plus its own radios and interval
       <select>. Our input is present in the server HTML and gone from the DOM
       by the time this runs.

       So we do not fight it for ownership. We look the field up fresh every
       time (Seal may swap the node again) and write to whichever input
       currently carries the name, which keeps Seal's state and ours in
       agreement. Seal's own visible widget is hidden in CSS, since our
       purchase cards are the UI. */
    function sellingPlanField() {
      return form ? form.querySelector('[name="selling_plan"]') : null;
    }
    var atc = $(root, '[data-uf-atc]');
    var atcLabel = $(root, '[data-uf-atc-label]');
    var atcPrice = $(root, '[data-uf-atc-price]');
    var stockEl = $(root, '[data-uf-stock]');
    var stockCfg = stockEl ? stockEl.dataset : {};

    /* ------------------------------------------------------- selected plan */

    function selectedPlanRadio() {
      return $$(root, '[data-uf-plan]').filter(function (r) { return r.checked; })[0] || null;
    }

    function selectedPlanId() {
      var radio = selectedPlanRadio();
      /* 'pack' is a one-time purchase at quantity N, not a selling plan. It has
         to return null here or its value would be posted as a selling_plan id
         and the add would fail. */
      if (!radio || radio.value === 'onetime' || radio.value === 'pack') return null;
      return radio.value;
    }

    /* ------------------------------------------------- multi-pack option

       The pack card buys N of the SAME variant in one line, so the saving is a
       quantity discount rather than a selling plan. Modelled here as a
       discounted UNIT price, which means everything downstream — the Add to
       Cart total, the per-serving rate, Afterpay — arrives at the right number
       without knowing packs exist. Unit price times quantity is still the
       total, it is just a smaller unit.
    */
    function packRadio() {
      return $$(root, '[data-uf-plan-pack]').filter(function (r) { return r.checked; })[0] || null;
    }

    /* The pack radio whether or not it is selected — the quantity discount it
       describes is a Shopify discount on QUANTITY, so it fires no matter which
       card is chosen. Needed to keep the button honest when someone steps the
       single tub up to three. */
    function packRadioAny() {
      return $(root, '[data-uf-plan-pack]');
    }

    function packRule() {
      var r = packRadioAny();
      if (!r) return null;
      var qty = parseInt(r.dataset.ufPackQty, 10);
      var keep = parseInt(r.dataset.ufPackKeep, 10);
      if (!(qty > 1) || !(keep >= 0 && keep <= 100)) return null;
      return { qty: qty, keep: keep };
    }

    /* Greys the stepper out on the pack card instead of hiding it. The pack is
       a fixed multiple, so its quantity is not the customer's to change; the
       other two cards keep a working stepper. */
    function syncQtyLock() {
      var wrap = $(root, '[data-uf-qty]');
      if (!wrap) return;
      var locked = !!packRadio();
      wrap.classList.toggle('is-locked', locked);

      /* THE INPUT IS readonly, NEVER disabled.

         A disabled form control is not submitted. Disabling this one meant the
         quantity field never reached /cart/add, so Shopify fell back to 1 and
         the 3-pack added a single tub — the price was right and the quantity
         was not.

         readonly keeps the value in the payload while stopping it being typed
         into. The buttons can be disabled safely: they are type="button" and
         carry no name, so they were never part of the submission. */
      $$(wrap, 'button').forEach(function (el) { el.disabled = locked; });
      var input = $(wrap, 'input');
      if (input) {
        input.readOnly = locked;
        input.disabled = false;
      }
    }

    /* MIXED-FLAVOUR PACK. One dropdown per tub on the pack card (HYDRATE).
       Absent everywhere pack_mix_enable is off, and then every function here
       reports "not active" and changes nothing. */
    var packMix = $(root, '[data-uf-pack-mix]');
    var packMixSelects = packMix ? $$(packMix, '[data-uf-pack-mix-select]') : [];

    function packMixActive() {
      return !!(packMixSelects.length && packRadio());
    }

    /* Tubs grouped into cart lines: [{ id, quantity }], in dropdown order. */
    function packMixItems() {
      var counts = {};
      var order = [];
      packMixSelects.forEach(function (select) {
        var id = select.value;
        if (!counts[id]) { counts[id] = 0; order.push(id); }
        counts[id] += 1;
      });
      return order.map(function (id) { return { id: id, quantity: counts[id] }; });
    }

    /* Discounted price of the chosen tubs, and whether every one can be bought. */
    function packMixTotals(keep) {
      var total = 0;
      var available = true;
      packMixSelects.forEach(function (select) {
        var v = data.variants[select.value];
        if (!v) { available = false; return; }
        total += packUnitPrice(v.price, keep);
        if (!v.available) available = false;
      });
      return { total: total, available: available };
    }

    /** N for the selected pack, or 1 when no pack is selected. */
    function packQuantity() {
      var r = packRadio();
      var n = r ? parseInt(r.dataset.ufPackQty, 10) : 1;
      return n > 0 ? n : 1;
    }


    /* Writes the pack size into the real quantity field, because that is what
       the form submits and what currentQuantity() reads. Selecting any other
       card puts it back to 1 — otherwise a customer who looked at the 3-pack
       and changed their mind buys three single tubs. */
    function syncPackQuantity() {
      var input = $(root, '.uf-pdp__qty-input');
      if (!input) return;
      var want = String(packQuantity());
      if (input.value !== want) {
        input.value = want;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ONE TUB AT THE PACK DISCOUNT, THE WAY SHOPIFY WORKS IT OUT.
       Checked against a live cart: the automatic discount is taken per unit and
       rounded DOWN to the cent, then multiplied. 25% of $79.95 is $19.9875, so
       each tub loses $19.98 and three come to $179.91 — rounding the total
       instead quoted $179.89, two cents under what checkout charged. */
    function packUnitPrice(price, keep) {
      return price - Math.floor((price * (100 - keep)) / 100);
    }

    /** Price for the current variant under the currently selected plan. */
    function activePrice(variant) {
      var pack = packRadio();
      if (pack) {
        var keep = parseInt(pack.dataset.ufPackKeep, 10);
        if (!(keep >= 0 && keep <= 100)) keep = 100;
        return packUnitPrice(variant.price, keep);
      }
      var planId = selectedPlanId();
      if (!planId) return variant.price;

      var allocation = (variant.sellingPlanAllocations || []).filter(function (a) {
        return String(a.sellingPlanId) === String(planId);
      })[0];

      /* Falling back to the one-time price is deliberate: a variant that has no
         allocation for the chosen plan must not show the previous variant's
         subscription price. */
      return allocation ? allocation.price : variant.price;
    }

    /* ------------------------------------------------------ stock urgency */

    function renderStock(variant) {
      if (!stockEl) return;

      var textEl = $(root, '[data-uf-stock-text]');
      var markerEl = $(root, '[data-uf-stock-marker]');

      function show(tier, copy, count) {
        if (!copy) return clear();

        if (textEl) textEl.textContent = copy.replace('{count}', count);
        stockEl.setAttribute('data-level', tier);

        if (markerEl) {
          var icon = stockCfg[tier + 'Icon'];
          markerEl.classList.toggle('has-icon', !!icon);
          markerEl.style.backgroundImage = icon ? 'url("' + icon + '")' : '';
        }
      }

      function clear() {
        if (textEl) textEl.textContent = '';
        stockEl.removeAttribute('data-level');
        if (markerEl) {
          markerEl.classList.remove('has-icon');
          markerEl.style.backgroundImage = '';
        }
      }

      clear();

      /* Tracking off: no real number exists, so show nothing rather than
         inventing one (spec Section 7). */
      if (!variant.inventoryManagement) return;

      var qty = variant.inventoryQuantity;

      /* Out of stock and not purchasable. The disabled Add to Cart already
         says so, but an explicit line makes the state unambiguous. */
      if (!variant.available) return show('soldout', stockCfg.soldoutText, qty);

      if (qty <= 0) {
        if (variant.inventoryPolicy === 'continue') {
          return show('backorder', stockCfg.backorderText, qty);
        }
        return show('soldout', stockCfg.soldoutText, qty);
      }

      var urgent = parseInt(stockCfg.urgentThreshold, 10);
      var moving = parseInt(stockCfg.movingThreshold, 10);

      /* An empty copy setting means "say nothing at this tier" rather than
         render a bare dot with no text. */
      if (qty <= urgent) return show('urgent', stockCfg.urgentText, qty);
      if (qty <= moving) return show('moving', stockCfg.movingText, qty);
      return show('plenty', stockCfg.plentyText, qty);
    }

    /* ------------------------------------------------------ plan card prices */

    /* ----------------------------------------- plans, per variant

       VARIANTS DO NOT HAVE TO SHARE SELLING PLANS. A 70-serve tub can refill
       on a different interval to a 100-serve one, which is the sensible way to
       set them up — and it broke this buy box. The subscribe radio and the
       frequency dropdown were both rendered from whichever variant the page
       happened to open on, so switching to a variant that does not carry that
       exact plan id made the whole subscription option disappear.

       So the options are rebuilt from the incoming variant every time. The
       choice is remembered by selling plan GROUP rather than by plan id,
       because the id differs per variant while the group is what the merchant
       actually set up — pick "every 30 days" on one flavour and you stay on
       the 30-day plan after switching, even though the allocation underneath
       is a different one.
    */
    var preferredGroupId = null;
    var preferredIndex = 0;

    function allocationsFor(variant) {
      return (variant && variant.sellingPlanAllocations) || [];
    }

    function rebuildPlans(variant) {
      var radio = $(root, "[data-uf-plan-subscribe]");
      if (!radio) return;

      var card = radio.closest(".uf-pdp__plan");
      var select = $(root, "[data-uf-plan-freq-select]");
      var allocations = allocationsFor(variant);

      /* No subscription on this variant at all — hide the option rather than
         leave a card behind that cannot be bought. */
      if (!allocations.length) {
        if (card) card.hidden = true;
        if (radio.checked) {
          var oneTime = $$(root, "[data-uf-plan]").filter(function (r) {
            return r.value === "onetime";
          })[0];
          if (oneTime) oneTime.checked = true;
        }
        applyPlanState();
        return;
      }

      if (card) card.hidden = false;

      /* First run: adopt whatever the server rendered, so a page that opens
         on a working variant keeps the merchant-chosen default plan. */
      if (preferredGroupId === null) {
        var startedOn = allocations.filter(function (a) {
          return String(a.sellingPlanId) === String(radio.value);
        })[0];
        preferredGroupId = startedOn
          ? startedOn.sellingPlanGroupId
          : allocations[0].sellingPlanGroupId;
        preferredIndex = startedOn ? allocations.indexOf(startedOn) : 0;
      }

      /* Group first — that is the same plan the merchant set up. Failing
         that, the same POSITION, because these lists are ordered the same way
         on every variant and the second entry is the size-matched interval:
         100 days on the big tub, 70 on the small one. Dropping to the first
         plan instead would quietly move somebody off the refill schedule that
         suits the tub they just chose. */
      var chosen =
        allocations.filter(function (a) {
          return String(a.sellingPlanGroupId) === String(preferredGroupId);
        })[0] ||
        allocations[preferredIndex] ||
        allocations[0];

      if (select) {
        select.innerHTML = "";
        allocations.forEach(function (a) {
          var opt = document.createElement("option");
          opt.value = a.sellingPlanId;
          opt.textContent = a.label || String(a.sellingPlanId);
          select.appendChild(opt);
        });
        select.value = String(chosen.sellingPlanId);
        /* One interval is not a choice. */
        select.hidden = allocations.length < 2;
        var freqPrefix = select.parentElement.querySelector(".uf-pdp__plan-freq-prefix");
        if (freqPrefix) freqPrefix.hidden = select.hidden;
      }

      radio.value = chosen.sellingPlanId;
      applyPlanState();
    }

    function renderPlanPrices(variant) {
      $$(root, '[data-uf-plan]').forEach(function (radio) {
        var card = radio.closest('.uf-pdp__plan');
        if (!card) return;

        /* The pack card's figures are computed in Liquid from the pack size and
           the discount, and there is no selling plan allocation to look them up
           from. Left alone, the branch below would find no allocation and
           rewrite it with single-tub numbers. */
        if (radio.dataset.ufPlanPack !== undefined) return;

        var priceEl = $(card, '.uf-pdp__plan-price');
        var compareEl = $(card, '.uf-pdp__plan-compare');
        var unitEl = $(card, '.uf-pdp__plan-unit');
        var price = variant.price;

        /* THE ONE-TIME CARD CARRIES THE PRODUCT'S OWN SALE PRICE.

           Different thing to the strikethrough on the subscribe card, which
           compares the plan price against the one-time price. This one is
           compare_at_price straight off the variant, and it has to move with
           the variant — flavours can be on sale independently. */
        if (radio.value === 'onetime') {
          var onetimeCompare = $(card, '[data-uf-onetime-compare]');
          if (onetimeCompare) {
            var was = variant.compareAtPrice;
            if (was && was > variant.price) {
              onetimeCompare.textContent = money(was);
              onetimeCompare.hidden = false;
            } else {
              onetimeCompare.hidden = true;
            }
          }

          /* The badge says the amount rather than making anyone subtract
             two numbers. Flavours can be discounted independently, so it
             recalculates per variant rather than trusting what was
             rendered. */
          var savingBadge = $(card, '[data-uf-onetime-saving]');
          if (savingBadge) {
            var wasPrice = variant.compareAtPrice;
            if (wasPrice && wasPrice > variant.price) {
              var label = savingBadge.dataset.saveLabel || '';
              savingBadge.textContent = (label ? label + ' ' : '') + compactMoney(wasPrice - variant.price);
              savingBadge.hidden = false;
            } else {
              savingBadge.hidden = true;
            }
          }
        }

        if (radio.value !== 'onetime') {
          var allocation = (variant.sellingPlanAllocations || []).filter(function (a) {
            return String(a.sellingPlanId) === String(radio.value);
          })[0];

          /* No allocation for this variant means the plan doesn't apply to it —
             hide the whole card rather than show a wrong price. */
          if (!allocation) {
            card.hidden = true;
            if (radio.checked) {
              var oneTime = $$(root, '[data-uf-plan]').filter(function (r) { return r.value === 'onetime'; })[0];
              if (oneTime) { oneTime.checked = true; syncPlanState(); }
            }
            return;
          }

          card.hidden = false;
          price = allocation.price;

          if (compareEl) {
            if (allocation.price < variant.price) {
              compareEl.textContent = money(variant.price);
              compareEl.hidden = false;
            } else {
              compareEl.hidden = true;
            }
          }
        }

        if (priceEl) priceEl.textContent = money(price);

        if (unitEl) {
          if (variant.servings > 0) {
            var perServing = money(Math.round(price / variant.servings)) + (unitEl.dataset.suffix || '');
            var servingsLabel = (unitEl.dataset.servingsLabel || '').replace('{count}', variant.servings);

            /* The rate lives in its own span so it can be styled as the
               argument rather than as a caption. Falling back to the flat
               string keeps this working against markup that predates the
               split. */
            var rateEl = unitEl.querySelector('[data-uf-rate]');
            var countEl = unitEl.querySelector('[data-uf-servings-count]');
            if (rateEl) {
              rateEl.textContent = perServing;
              if (countEl) countEl.textContent = servingsLabel;
            } else {
              unitEl.textContent = servingsLabel ? servingsLabel + ' · ' + perServing : perServing;
            }
            unitEl.hidden = false;
          } else {
            /* No pdp_servings on this variant — hide rather than divide by zero
               or print a servings count we don't actually have. */
            unitEl.hidden = true;
          }
        }
      });
    }

    /* --------------------------------------------------------- main update */

    function currentVariant() {
      return data.variants[currentVariantId];
    }

    /* SPLIT IN TWO, because one half of this is safe to call from inside
       update() and the other half is not.

       applyPlanState() writes what the DOM should show for the plan that is
       currently chosen. syncPlanState() does that AND re-runs update(),
       which is right when a customer picks a plan and wrong when update() is
       already running — rebuildPlans() is called from update(), so calling
       the second one from there recursed until the stack gave out. */
    /* Which card was selected last time applyPlanState ran. Starts null so the
       first pass always syncs, which is what pre-selects the pack at 3. */
    var lastSelection = null;

    function applyPlanState() {
      $$(root, '.uf-pdp__plan').forEach(function (card) {
        var radio = $(card, '[data-uf-plan]');
        card.classList.toggle('is-selected', !!(radio && radio.checked));
      });

      /* ONLY WHEN THE SELECTION ACTUALLY CHANGES.

         Forcing the quantity on every applyPlanState() call broke the stepper:
         changing the quantity fires update(), update() calls applyPlanState(),
         and that reset the field straight back to 1. The customer pressed "+"
         and nothing happened.

         So it runs on a genuine change of card — pack sets 3, anything else
         sets 1 — and leaves the field alone the rest of the time. Before the
         price is read, not after, because update() multiplies by it. */
      if (packMix) packMix.hidden = !packRadio();

      var selectionNow = (selectedPlanRadio() || {}).value || '';
      if (selectionNow !== lastSelection) {
        lastSelection = selectionNow;
        syncPackQuantity();
      }

      var planId = selectedPlanId();
      var planInput = sellingPlanField();
      if (planInput) {
        planInput.value = planId || '';
        planInput.disabled = !planId;
        /* Seal listens for this to keep its own state in step. */
        planInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      syncTerms();
    }

    /* ------------------------------------ condensed subscription consent */

    /* Shopify injects #shopify-buyer-consent into the payment button some time
       after load, and re-renders that button, so it is looked up every time
       rather than cached. */
    var terms = $(root, '[data-uf-terms]');
    var termsToggle = terms && $(terms, '[data-uf-terms-toggle]');
    var termsInfo = terms && $(terms, '[data-uf-terms-info]');

    function shopifyConsent() {
      return $(root, '#shopify-buyer-consent');
    }

    function closeTerms() {
      if (!termsInfo) return;
      termsInfo.hidden = true;
      termsToggle.setAttribute('aria-expanded', 'false');
    }

    function syncTerms() {
      if (!terms) return;
      var consent = shopifyConsent();
      /* Hide Shopify's paragraph only once there is a line to replace it. */
      if (consent) root.classList.add('uf-pdp--terms-ready');
      var show = !!(consent && selectedPlanId());
      terms.hidden = !show;
      if (!show) closeTerms();
    }

    function fillTerms() {
      var consent = shopifyConsent();
      if (!consent) return false;
      termsInfo.innerHTML = '';

      var body = document.createElement('p');
      body.className = 'uf-pdp__terms-body';
      body.innerHTML = consent.innerHTML;
      /* The copy must not carry Shopify's ids, and "cancellation policy" has to
         keep working: it forwards to Shopify's own button, which opens the
         policy wherever Shopify wired it. */
      Array.prototype.forEach.call(body.querySelectorAll('[id]'), function (el) {
        var original = document.getElementById(el.id);
        el.removeAttribute('id');
        if (!original) return;
        var link = document.createElement('button');
        link.type = 'button';
        link.className = 'uf-pdp__terms-policy';
        link.textContent = el.textContent;
        link.addEventListener('click', function (event) {
          event.stopPropagation();
          closeTerms();
          original.click();
        });
        el.replaceWith(link);
      });
      termsInfo.appendChild(body);

      /* Seal's "How subscriptions work" note, when its widget has rendered —
         this is where the store's minimum term lives. Heading and wording
         only: its trailing link goes to Seal's own marketing site. */
      var seal = document.querySelector('.sls-more-info-popup-content');
      if (seal) {
        var heading = '';
        var words = [];
        Array.prototype.forEach.call(seal.childNodes, function (node) {
          if (node.nodeName === 'STRONG' || node.nodeName === 'B') heading = node.textContent.trim();
          else if (node.nodeType === 3) words.push(node.textContent);
        });
        var sealText = words.join(' ').replace(/\s+/g, ' ').trim();
        if (sealText) {
          var note = document.createElement('p');
          note.className = 'uf-pdp__terms-body';
          if (heading) {
            var strong = document.createElement('strong');
            strong.className = 'uf-pdp__terms-heading';
            strong.textContent = heading;
            note.appendChild(strong);
          }
          note.appendChild(document.createTextNode(sealText));
          termsInfo.appendChild(note);
        }
      }
      return true;
    }

    if (terms && termsToggle && termsInfo) {
      termsToggle.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (!termsInfo.hidden) { closeTerms(); return; }
        if (!fillTerms()) return;
        termsInfo.hidden = false;
        termsToggle.setAttribute('aria-expanded', 'true');
      });

      termsInfo.addEventListener('click', function (event) {
        event.stopPropagation();
      });

      document.addEventListener('click', closeTerms);
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeTerms();
      });

      /* The consent paragraph arrives after load — watch for it once. */
      var paymentWrap = $(root, '.uf-pdp__dynamic-checkout');
      if (paymentWrap && 'MutationObserver' in window) {
        var consentWatch = new MutationObserver(function () {
          if (shopifyConsent()) syncTerms();
        });
        consentWatch.observe(paymentWrap, { childList: true, subtree: true });
      }

      /* applyPlanState() may already have run before these were assigned. */
      syncTerms();
    }

    function syncPlanState() {
      applyPlanState();
      update();
    }

    function update() {
      var variant = currentVariant();
      if (!variant) return;

      if (variantInput) variantInput.value = variant.id;

      var unitPrice = activePrice(variant);
      /* The button states what's about to be added, so it reflects quantity.
         The per-unit rates on the purchase cards deliberately do NOT — those
         are rates, and multiplying them would be wrong. */
      /* THE BUTTON PRICES WHAT THE CART WILL CHARGE.

         The quantity discount is a Shopify automatic discount on quantity,
         scoped to one-time purchases — so it applies to three tubs however the
         customer got there, whether they picked the pack card or stepped the
         single tub up to three. Quoting the undiscounted total in that case
         would mean the button says one number and checkout says another.

         Subscriptions are excluded because the discount is, so three tubs on a
         plan is simply three times the plan price.

         activePrice stays a UNIT price either way — the per-serving rates
         divide by servings-per-tub, and a discounted unit is still a unit. */
      var qty = currentQuantity();
      var rule = packRule();
      var buttonPrice;
      if (rule && !selectedPlanId() && qty >= rule.qty) {
        buttonPrice = packUnitPrice(variant.price, rule.keep) * qty;
      } else {
        buttonPrice = unitPrice * qty;
      }

      /* A mixed pack prices and stocks-checks the tubs actually chosen, not
         the flavour on screen — someone viewing a sold-out flavour can still
         buy three of the others. */
      var canBuy = variant.available;
      if (rule && packMixActive()) {
        var mix = packMixTotals(rule.keep);
        buttonPrice = mix.total;
        canBuy = mix.available;
      }

      syncQtyLock();

      if (atc) atc.disabled = !canBuy;
      if (atcLabel) atcLabel.textContent = canBuy ? (atc.dataset.labelAvailable || atcLabel.textContent) : (atc.dataset.labelSoldOut || atcLabel.textContent);
      if (atcPrice) {
        atcPrice.textContent = money(buttonPrice);
        atcPrice.hidden = !canBuy;
      }

      rebuildPlans(variant);
      renderPlanPrices(variant);
      renderStock(variant);
      renderServings(variant);
      renderFlavourNote(variant);
      syncAfterpay(buttonPrice);
    }

    /** The standalone spec line. Collapses when the variant has no servings. */
    function renderServings(variant) {
      var el = $(root, '[data-uf-servings]');
      if (!el) return;

      var template = el.dataset.template || '';
      el.textContent = variant.servings > 0 && template
        ? template.replace('{count}', variant.servings)
        : '';
    }

    /**
     * The flavour note under the swatches.
     *
     * Collapsed with hidden rather than emptied, so a product with notes on
     * only some of its flavours doesn't leave a gap where the others were —
     * the swatches shift up instead.
     */
    function renderFlavourNote(variant) {
      var el = $(root, '[data-uf-flavour-note]');
      if (!el) return;

      var note = variant.flavourNote;
      var textEl = el.querySelector('[data-uf-flavour-note-text]');

      if (!note) {
        el.hidden = true;
        return;
      }

      if (textEl) textEl.textContent = note;
      el.hidden = false;

      /* Restart the fade so the change registers when someone switches between
         two flavours that both have a note. Without the reflow the class is
         removed and re-added inside one frame and the browser never runs it. */
      el.classList.remove('is-fresh');
      void el.offsetWidth;
      el.classList.add('is-fresh');
    }

    function currentQuantity() {
      var input = $(root, '.uf-pdp__qty-input');
      var n = input ? parseInt(input.value, 10) : 1;
      return !n || n < 1 ? 1 : n;
    }

    /**
     * Keep the Afterpay instalment figure in step with the active price.
     *
     * snippets/buy-buttons.liquid does this by reading the button's text back
     * out of the DOM and parsing digits out of it. We already hold the exact
     * amount in cents, so we hand it over directly — no parsing, no currency
     * formatting to get wrong, no 500ms guess at when the DOM settled.
     */
    function syncAfterpay(cents) {
      var anchor = $(root, '[data-uf-afterpay-anchor]');
      if (anchor) anchor.textContent = money(cents);

      var widget = window.afterpay_product_widget;
      if (widget && typeof widget.update === 'function') {
        widget.update({ amount: cents });
      }
    }

    /* ------------------------------------------------- Phase 3: flavour */

    $$(root, '[data-uf-flavour]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        currentVariantId = String(input.dataset.variantId);

        /* Keep the URL honest so refresh/share lands on the same variant,
           without a navigation. */
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', currentVariantId);
          window.history.replaceState({}, '', url);
        } catch (e) { /* non-fatal */ }

        var variant = currentVariant();
        if (variant && variant.featuredMediaId) showMedia(variant.featuredMediaId);

        update();
      });
    });

    /**
     * Point the mobile hero at a given media id. Null media = leave it alone.
     *
     * Never scrolls the page. An earlier version called scrollIntoView() on the
     * desktop grid tile, which nudged the whole document down on every variant
     * change — and the grid shows every tile at once, so there was nothing to
     * scroll to in the first place.
     */
    function showMedia(mediaId) {
      var thumb = root.querySelector('[data-uf-thumb][data-media-id="' + mediaId + '"]');
      if (thumb) selectThumb(thumb);
    }

    function selectThumb(thumb) {
      var heroImg = $(root, '.uf-pdp__hero-img');
      if (heroImg && thumb.dataset.full) {
        /* srcset is cleared so the browser can't keep serving a candidate from
           the previous image's set once src changes. */
        heroImg.removeAttribute('srcset');
        heroImg.removeAttribute('sizes');
        heroImg.src = thumb.dataset.full;
      }

      $$(root, '[data-uf-thumb]').forEach(function (t) {
        t.classList.toggle('is-selected', t === thumb);
      });
    }

    $$(root, '[data-uf-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () { selectThumb(thumb); });
    });

    /* -------------------------------------------- Phase 4: purchase options */

    $$(root, '[data-uf-plan]').forEach(function (radio) {
      radio.addEventListener('change', syncPlanState);
    });

    /* Delivery frequency, when there is more than one plan to choose between.

       This used to look for a radio whose value matched the chosen plan, which
       assumed a radio per plan. There is one subscribe radio, and the dropdown
       retargets it — so the plan id is written onto the radio rather than
       searched for. renderPlanPrices() reads that value back to find the
       allocation, so the card price, the compare-at and the per-serving rate
       all follow without any of them being updated here.

       Selecting an interval also selects the subscription: nobody changes the
       delivery frequency of a purchase they intend to make once. */
    var freqSelect = $(root, '[data-uf-plan-freq-select]');
    if (freqSelect) {
      freqSelect.addEventListener('change', function () {
        var radio = $(root, '[data-uf-plan-subscribe]');
        if (!radio) return;
        radio.value = freqSelect.value;
        radio.checked = true;

        /* By group, so the same interval survives a variant change even
           though the plan id underneath will be a different one. */
        var picked = allocationsFor(currentVariant()).filter(function (a) {
          return String(a.sellingPlanId) === String(freqSelect.value);
        })[0];
        if (picked) {
          preferredGroupId = picked.sellingPlanGroupId;
          preferredIndex = allocationsFor(currentVariant()).indexOf(picked);
        }

        syncPlanState();
      });
    }

    /* ------------------------------------------ mixed-flavour pack: events */

    if (packMixSelects.length) {
      packMixSelects.forEach(function (select) {
        select.addEventListener('change', function () {
          select.dataset.ufTouched = 'true';
          update();
        });
      });

      /* Picking a flavour swatch moves every tub the customer has not set by
         hand, so "three of this one" follows the swatch, but a mix they have
         built is never undone by browsing the flavours. */
      $$(root, '[data-uf-flavour]').forEach(function (input) {
        input.addEventListener('change', function () {
          if (!input.checked) return;
          var id = String(input.dataset.variantId);
          packMixSelects.forEach(function (select) {
            if (select.dataset.ufTouched) return;
            var option = Array.prototype.filter.call(select.options, function (o) { return o.value === id; })[0];
            if (option && !option.disabled) select.value = id;
          });
          update();
        });
      });
    }

    /* THE MIXED PACK IS ADDED HERE, NOT BY THE THEME'S FORM.

       The theme's product-form posts one variant id and one quantity, which
       cannot say "2 Lemon & Lime and 1 Pineapple". So while the mix is showing,
       the submit is caught on the way down (capture, on an ancestor, so it runs
       before the form's own listener) and the tubs are posted as items[].

       Everything after the request copies the theme's ProductForm exactly —
       bundled sections, variant:add to open the drawer, cart:change to refresh
       it, cart:error on failure — so the drawer cannot tell the difference.
       The sticky bar clicks the real button, so it arrives here too. */
    var packMixBusy = false;

    function addPackMix() {
      if (packMixBusy) return;
      var items = packMixItems();
      if (!items.length) return;
      packMixBusy = true;
      if (atc) atc.setAttribute('aria-busy', 'true');

      var sections = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sections }
      }));

      var body = new FormData();
      items.forEach(function (item, i) {
        body.append('items[' + i + '][id]', item.id);
        body.append('items[' + i + '][quantity]', String(item.quantity));
      });
      body.set('sections', sections.join(','));
      body.set('sections_url', window.Shopify.routes.root + 'variants/' + items[0].id);

      function done() {
        packMixBusy = false;
        if (atc) atc.removeAttribute('aria-busy');
      }

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: body
      })
        .then(function (res) { return res.json().then(function (json) { return { ok: res.ok, json: json }; }); })
        .then(function (result) {
          done();
          if (!result.ok) {
            form.dispatchEvent(new CustomEvent('cart:error', {
              bubbles: true,
              detail: { error: result.json && result.json.description }
            }));
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            return;
          }

          var settings = (window.themeVariables && window.themeVariables.settings) || {};
          if (settings.cartType === 'page' || settings.pageType === 'cart') {
            window.location.href = window.Shopify.routes.root + 'cart';
            return;
          }

          return fetch(window.Shopify.routes.root + 'cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              cart.sections = result.json.sections;
              form.dispatchEvent(new CustomEvent('variant:add', {
                bubbles: true,
                detail: {
                  items: result.json.hasOwnProperty('items') ? result.json.items : [result.json],
                  cart: cart
                }
              }));
              document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                bubbles: true,
                detail: { baseEvent: 'variant:add', cart: cart }
              }));
            });
        })
        .catch(function () {
          done();
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        });
    }

    if (form && packMixSelects.length) {
      root.addEventListener('submit', function (event) {
        if (event.target !== form || !packMixActive()) return;
        event.preventDefault();
        event.stopPropagation();
        addPackMix();
      }, true);
    }

    /* ------------------------------------------------- Phase 5: quantity */

    var qtyInput = $(root, '.uf-pdp__qty-input');

    function stepQty(delta) {
      if (!qtyInput) return;
      var next = (parseInt(qtyInput.value, 10) || 1) + delta;
      qtyInput.value = Math.max(1, next);
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    var down = $(root, '[data-uf-qty-down]');
    var up = $(root, '[data-uf-qty-up]');
    if (down) down.addEventListener('click', function () { stepQty(-1); });
    if (up) up.addEventListener('click', function () { stepQty(1); });

    if (qtyInput) {
      /* 'input' as well as 'change' so typing a quantity updates the button
         immediately rather than only on blur. */
      ['input', 'change'].forEach(function (evt) {
        qtyInput.addEventListener(evt, function () {
          var n = parseInt(qtyInput.value, 10);
          if (evt === 'change' && (!n || n < 1)) qtyInput.value = 1;
          update();
        });
      });
    }

    /* ---------------------------------------------- Phase 6: pill popovers */

    function closeAllPills(except) {
      $$(root, '[data-uf-pill]').forEach(function (btn) {
        if (btn === except) return;
        btn.setAttribute('aria-expanded', 'false');
        var panel = btn.parentElement.querySelector('[data-uf-pill-info]');
        if (panel) panel.hidden = true;
      });
    }

    $$(root, '[data-uf-pill]').forEach(function (btn) {
      var panel = btn.parentElement.querySelector('[data-uf-pill-info]');
      if (!panel) return;

      btn.addEventListener('click', function (event) {
        event.stopPropagation();
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        closeAllPills(btn);
        btn.setAttribute('aria-expanded', String(!isOpen));
        panel.hidden = isOpen;
      });
    });

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target) || !event.target.closest('[data-uf-pill]')) {
        closeAllPills(null);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeAllPills(null);
    });

    /* ------------------------------------------- Phase 7: accordion motion */

    /* Single-open group: opening one closes the rest. Note this departs from
       spec Section 8, which said independent toggles were fine because Bloom
       doesn't enforce single-open — Cam's call, 2026-08-06. */
    var accordions = $$(root, '.uf-pdp__accordion').map(function (details) {
      var body = $(details, '.uf-pdp__accordion-body');
      var summary = $(details, '.uf-pdp__accordion-summary');
      if (!body || !summary) return null;

      /* <details> snaps open instantly. Animate grid-template-rows 0fr -> 1fr
         so the panel expands to its own height without measuring it. */
      body.style.gridTemplateRows = details.open ? '1fr' : '0fr';
      body.style.transition = 'grid-template-rows 200ms cubic-bezier(0.4, 0, 0.2, 1)';

      var closeTimer = null;

      /* Our own state, NOT details.open.
         details.open has to stay true for the whole close animation (the panel
         must remain in the layout while it collapses), so during those 200ms
         it reports "open" while the accordion is visibly closing. Deciding
         what a click means from that flag meant a click mid-close was read as
         "close it again" — the accordion just sat there looking broken. */
      var state = details.open ? 'open' : 'closed';

      function clearPending() {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
      }

      function open() {
        clearPending();
        state = 'open';
        details.open = true;
        requestAnimationFrame(function () {
          body.style.gridTemplateRows = '1fr';
        });
      }

      function close() {
        if (state === 'closed') return;
        clearPending();
        state = 'closed';
        body.style.gridTemplateRows = '0fr';
        /* A timer rather than transitionend: the event doesn't fire when the
           row is already at 0fr, which would strand details.open = true. */
        closeTimer = setTimeout(function () {
          /* Re-check: the panel may have been reopened while collapsing. */
          if (state === 'closed') details.open = false;
          closeTimer = null;
        }, 200);
      }

      var api = { details: details, open: open, close: close, isOpen: function () { return state === 'open'; } };

      summary.addEventListener('click', function (event) {
        event.preventDefault();

        if (state === 'open') {
          close();
          return;
        }

        accordions.forEach(function (other) {
          if (other && other !== api) other.close();
        });

        open();
      });

      return api;
    });

    /* --------------------------------------------- Phase 8: cross-sell box */

    /* Full ARIA tabs pattern. The markup carries the id / aria-controls /
       aria-labelledby wiring; this supplies the behaviour those roles promise:
       roving tabindex so only the active tab is in the tab order, and
       arrow / Home / End to move between them. Activation follows focus, which
       is the right choice here because switching panels is instant. */
    var tabs = $$(root, '[data-uf-pair-tab]');

    function activateTab(tab, moveFocus) {
      var name = tab.dataset.ufPairTab;

      tabs.forEach(function (t) {
        var on = t === tab;
        t.classList.toggle('is-selected', on);
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
      });

      /* A private attribute rather than the hidden property. The theme sets
         [hidden] { display: none !important }, which cannot be animated and
         cannot be overridden without another !important — and the card now
         animates between the two panel heights. Marking a panel hidden while
         CSS kept it displayed would also have been a lie to assistive tech;
         the stylesheet uses visibility to take the inactive panel out of the
         accessibility tree and the tab order instead. */
      $$(root, '[data-uf-pair-panel]').forEach(function (panel) {
        panel.toggleAttribute('data-uf-pair-hidden', panel.dataset.ufPairPanel !== name);
      });

      /* The card animates between the two panel heights, and CSS drives that
         off this attribute rather than off which panel is [hidden] — a
         stylesheet can react to an attribute on the ancestor, but it cannot
         size a container from the height of one particular child. Setting it
         here keeps the whole transition declarative. */
      var box = tab.closest('[data-uf-pair]');
      if (box) box.setAttribute('data-uf-pair-view', name);

      if (moveFocus) tab.focus();
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () { activateTab(tab, false); });

      tab.addEventListener('keydown', function (event) {
        var next = null;

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = tabs[(index + 1) % tabs.length];
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = tabs[(index - 1 + tabs.length) % tabs.length];
            break;
          case 'Home':
            next = tabs[0];
            break;
          case 'End':
            next = tabs[tabs.length - 1];
            break;
          default:
            return;
        }

        event.preventDefault();
        activateTab(next, true);
      });
    });

    $$(root, '[data-uf-pair-card]').forEach(function (card) {
      var select = $(card, '[data-uf-pair-variant]');
      var button = $(card, '[data-uf-pair-atc]');
      if (!button) return;

      if (select) {
        select.addEventListener('change', function () {
          button.dataset.variantId = select.value;
        });
      }

      button.addEventListener('click', function () {
        var id = button.dataset.variantId;
        if (!id) return;

        button.setAttribute('aria-busy', 'true');

        /* Adds the OTHER product, not the PDP's.

           THE CART DRAWER NEEDS TWO THINGS WE WERE NOT GIVING IT, and missing
           either one leaves the item in the cart with no drawer to show for it:

           1. THE BUNDLED SECTION MARKUP. The theme's own ProductForm asks
              cart/add.js for rendered sections and hands them back on the cart
              object; the drawer then re-renders from
              cart.sections[its own id]. Adding without them left that
              undefined, so the drawer's own change handler threw.

           2. A `variant:add` EVENT ON document. `cart:change` only updates the
              drawer's contents — `variant:add` is what OPENS it. We dispatched
              only the first, so the cart silently gained an item.

           The section list is collected the same way the theme collects it, by
           asking the page rather than hardcoding ids, so anything else that
           wants re-rendering on add keeps working. */
        var sections = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sections }
        }));

        var body = new FormData();
        body.set('id', id);
        body.set('quantity', '1');
        body.set('sections', sections.join(','));
        body.set('sections_url', window.Shopify.routes.root + 'variants/' + id);

        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: body
        })
          .then(function (res) { return res.json().then(function (json) { return { ok: res.ok, json: json }; }); })
          .then(function (result) {
            button.removeAttribute('aria-busy');
            if (!result.ok) {
              document.dispatchEvent(new CustomEvent('cart:refresh'));
              return;
            }
            /* A 200 with nothing in it means something swallowed the add — a
               bundle app, or a cart transform. It is indistinguishable from
               success at the HTTP level and leaves the customer looking at a
               button that did nothing, so send them where the product's own
               flow lives instead. */
            var added = result.json && (result.json.id || result.json.items);
            if (!added) {
              var url = button.dataset.productUrl;
              if (url) {
                window.location.href = url;
                return;
              }
              document.dispatchEvent(new CustomEvent('cart:refresh'));
              return;
            }

            return fetch(window.Shopify.routes.root + 'cart.js')
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                cart.sections = result.json.sections;

                var items = result.json.hasOwnProperty('items')
                  ? result.json.items
                  : [result.json];

                document.dispatchEvent(new CustomEvent('variant:add', {
                  bubbles: true,
                  detail: { items: items, cart: cart }
                }));

                document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                  bubbles: true,
                  detail: { baseEvent: 'variant:add', cart: cart }
                }));
              });
          })
          .catch(function () {
            button.removeAttribute('aria-busy');
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          });
      });
    });

    /* ----------------------------------------------------------- first paint */

    syncPlanState();

    /* Calibrate after load, once any currency script has had its turn — at
       DOMContentLoaded the price may not have been rewritten yet. Re-render
       afterwards so the button carries the learned format from then on. */
    function calibrateAndRefresh() {
      calibrateMoney();
      update();
    }

    if (document.readyState === 'complete') {
      setTimeout(calibrateAndRefresh, 1200);
    } else {
      window.addEventListener('load', function () {
        setTimeout(calibrateAndRefresh, 1200);
      });
    }
  }

  function init() {
    $$(document, '[data-uf-pdp]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders a section without reloading the page. */
  document.addEventListener('shopify:section:load', function (event) {
    $$(event.target, '[data-uf-pdp]').forEach(initSection);
  });
})();
